import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { UserPlus, CheckCircle2, Circle, Clock, X, Trash2, Plus, ChevronRight, FileText, Shield } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';
import OnboardingFormModal from '../components/OnboardingFormModal';
import OnboardingHrPanel from '../components/OnboardingHrPanel';

// ═══════════════════════════════════════════════════════════════════
//  STAFF ONBOARDING PAGE
//  New staff joining workflow — 22-item checklist (documents, training,
//  observation, kit, legal, signoff). Feeds into HRM SOP compliance.
// ═══════════════════════════════════════════════════════════════════

const CATEGORY_COLOR = {
  documents: theme.info, training: theme.purple, observation: theme.warning,
  kit: theme.success, legal: theme.danger, signoff: theme.primary,
};

const CATEGORY_ICON = {
  documents: '📄', training: '🎓', observation: '👀',
  kit: '🎒', legal: '📜', signoff: '✍️',
};

const StaffOnboardingPage = ({ API_BASE, currentUser }) => {
  const [records, setRecords] = useState([]);
  const [active, setActive] = useState(null);   // currently expanded record
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ fullName: '', role: 'nurse', joiningDate: new Date().toISOString().slice(0, 10), mentorName: '' });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [showFullForm, setShowFullForm] = useState(false);
  const [showHrPanel, setShowHrPanel]   = useState(false);

  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);
  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/onboarding`);
      setRecords(res.data || []);
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const createRecord = async () => {
    if (!form.fullName) return showMsg('error', 'Staff name required');
    try {
      await axios.post(`${API_BASE}/onboarding`, form);
      showMsg('success', 'Onboarding record created');
      setShowForm(false);
      setForm({ fullName: '', role: 'nurse', joiningDate: new Date().toISOString().slice(0, 10), mentorName: '' });
      load();
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
  };

  const toggleItem = async (recordId, itemKey, newStatus) => {
    try {
      const res = await axios.post(`${API_BASE}/onboarding/${recordId}/item/${itemKey}`, {
        status: newStatus,
        completedBy: currentUser?.fullName || currentUser?.username,
      });
      setRecords(prev => prev.map(r => r._id === recordId ? res.data : r));
      if (active?._id === recordId) setActive(res.data);
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm('Onboarding record delete karein?')) return;
    try {
      await axios.delete(`${API_BASE}/onboarding/${id}`);
      load();
      setActive(null);
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
  };

  return (
    <div>
      {/* HEADER */}
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <UserPlus size={32} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.35rem' }}>Staff Onboarding</h2>
            <p style={{ margin: '2px 0 0', opacity: 0.9, fontSize: 13 }}>
              New staff joining workflow — 22-item checklist (NABH HRM compliance)
            </p>
          </div>
          {isAdmin && (
            <button onClick={() => setShowForm(true)} style={{ ...btnStyle, marginTop: 0, backgroundColor: 'white', color: theme.primaryDark }}>
              <Plus size={16} /> <span style={{ marginLeft: 6 }}>New Onboarding</span>
            </button>
          )}
        </div>
      </div>

      {msg.text && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: active ? '340px 1fr' : '1fr', gap: 14, alignItems: 'start' }}>
        {/* LIST */}
        <div>
          {loading ? (
            <div style={{ ...cardStyle, padding: 30, textAlign: 'center', color: theme.textMuted }}>Loading…</div>
          ) : records.length === 0 ? (
            <div style={{ ...cardStyle, padding: 50, textAlign: 'center' }}>
              <UserPlus size={40} color={theme.textSubtle} style={{ opacity: 0.5 }} />
              <p style={{ color: theme.textMuted, marginTop: 10, fontWeight: 600 }}>Koi onboarding record nahi</p>
              <p style={{ fontSize: 13, color: theme.textSubtle }}>"New Onboarding" dabayein</p>
            </div>
          ) : (
            records.map(r => (
              <div key={r._id}
                onClick={() => setActive(r)}
                style={{
                  ...cardStyle, padding: 14, marginBottom: 8, cursor: 'pointer',
                  borderLeft: `4px solid ${r.completionPct === 100 ? theme.success : r.completionPct >= 50 ? theme.warning : theme.info}`,
                  background: active?._id === r._id ? theme.primaryLight + '40' : 'white',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 20,
                    background: theme.primary + '22', color: theme.primary,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 16,
                  }}>
                    {r.fullName.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: theme.navy, fontSize: 14 }}>{r.fullName}</div>
                    <div style={{ fontSize: 12, color: theme.textMuted }}>
                      {r.role} · joined {new Date(r.joiningDate).toLocaleDateString('en-IN')}
                    </div>
                    {r.mentorName && <div style={{ fontSize: 11.5, color: theme.textSubtle }}>Mentor: {r.mentorName}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: 15, fontWeight: 800,
                      color: r.completionPct === 100 ? theme.success : theme.primary,
                    }}>
                      {r.completionPct}%
                    </div>
                    {r.submission?.stage && r.submission.stage !== 'draft' && (
                      <div style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, marginTop: 2, display: 'inline-block',
                        background: r.submission.stage === 'approved' ? theme.success + '22'
                          : r.submission.stage === 'rejected' ? theme.danger + '22'
                          : theme.warning + '22',
                        color: r.submission.stage === 'approved' ? theme.success
                          : r.submission.stage === 'rejected' ? theme.danger : theme.warning,
                      }}>
                        {r.submission.stage.toUpperCase()}
                      </div>
                    )}
                    <ChevronRight size={18} color={theme.textSubtle} />
                  </div>
                </div>
                {/* Progress bar */}
                <div style={{ marginTop: 10, height: 6, background: theme.borderLight, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    width: `${r.completionPct}%`, height: '100%',
                    background: r.completionPct === 100 ? theme.success : theme.primary,
                    transition: 'width 0.3s',
                  }} />
                </div>
              </div>
            ))
          )}
        </div>

        {/* DETAIL PANEL */}
        {active && (
          <div style={{ ...cardStyle, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <h3 style={{ margin: 0, color: theme.navy }}>{active.fullName}</h3>
                <p style={{ margin: '3px 0 0', fontSize: 13, color: theme.textMuted }}>
                  {active.role} · {new Date(active.joiningDate).toLocaleDateString('en-IN')}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button onClick={() => setShowFullForm(true)} style={{ ...btnStyle, padding: '6px 10px', fontSize: 12, background: theme.info }}>
                  <FileText size={13} style={{ marginRight: 4 }}/> Full Form
                </button>
                {isAdmin && (
                  <button onClick={() => setShowHrPanel(true)} style={{ ...btnStyle, padding: '6px 10px', fontSize: 12, background: theme.warning }}>
                    <Shield size={13} style={{ marginRight: 4 }}/> HR Verify
                  </button>
                )}
                {isAdmin && (
                  <button onClick={() => deleteRecord(active._id)} style={{ border: `1px solid ${theme.danger}33`, background: theme.danger + '12', color: theme.danger, padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>
                    <Trash2 size={14} />
                  </button>
                )}
                <button onClick={() => setActive(null)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                  <X size={20} color={theme.textMuted} />
                </button>
              </div>
            </div>

            {/* Progress summary */}
            <div style={{
              padding: '12px 14px', borderRadius: 8, marginBottom: 14,
              background: active.completionPct === 100 ? '#d1fae5' : '#fffbeb',
              color: active.completionPct === 100 ? '#065f46' : '#92400e',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>
                  {active.completionPct === 100 ? '🎉 Onboarding complete!' : `${active.completionPct}% complete`}
                </span>
                <span>
                  {active.checklist.filter(i => i.status === 'completed' || i.status === 'na').length} / {active.checklist.length}
                </span>
              </div>
            </div>

            {/* Checklist grouped by category */}
            {Object.entries(groupByCategory(active.checklist)).map(([cat, items]) => (
              <div key={cat} style={{ marginBottom: 14 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                  fontSize: 11, fontWeight: 700, color: CATEGORY_COLOR[cat] || theme.textMuted,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  <span style={{ fontSize: 14 }}>{CATEGORY_ICON[cat] || '📌'}</span>
                  {cat} ({items.filter(i => i.status === 'completed').length}/{items.length})
                </div>
                {items.map(it => (
                  <div key={it.key} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 11px', borderRadius: 7, marginBottom: 4,
                    background: it.status === 'completed' ? '#f0fdf4' : it.status === 'na' ? '#f3f4f6' : 'white',
                    border: `1px solid ${it.status === 'completed' ? theme.success + '55' : theme.border}`,
                  }}>
                    <button
                      onClick={() => toggleItem(active._id, it.key, it.status === 'completed' ? 'pending' : 'completed')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      {it.status === 'completed'
                        ? <CheckCircle2 size={20} color={theme.success} />
                        : it.status === 'na'
                          ? <Clock size={20} color={theme.textSubtle} />
                          : <Circle size={20} color={theme.textSubtle} />}
                    </button>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 13.5,
                        color: it.status === 'completed' ? theme.textMuted : theme.navy,
                        textDecoration: it.status === 'completed' ? 'line-through' : 'none',
                      }}>
                        {it.label}
                      </div>
                      {it.completedAt && (
                        <div style={{ fontSize: 11, color: theme.textSubtle }}>
                          {new Date(it.completedAt).toLocaleDateString('en-IN')}{it.completedBy ? ` · ${it.completedBy}` : ''}
                        </div>
                      )}
                    </div>
                    <select
                      value={it.status}
                      onChange={e => toggleItem(active._id, it.key, e.target.value)}
                      style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: 11.5 }}>
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="na">N/A</option>
                    </select>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FULL FORM MODAL */}
      {showFullForm && active && (
        <OnboardingFormModal
          API_BASE={API_BASE}
          currentUser={currentUser}
          record={active}
          onClose={() => setShowFullForm(false)}
          onUpdate={(updated) => {
            setActive(updated);
            setRecords(prev => prev.map(r => r._id === updated._id ? updated : r));
          }}
        />
      )}

      {/* HR VERIFICATION PANEL */}
      {showHrPanel && active && (
        <OnboardingHrPanel
          API_BASE={API_BASE}
          currentUser={currentUser}
          record={active}
          onClose={() => setShowHrPanel(false)}
          onUpdate={(updated) => {
            setActive(updated);
            setRecords(prev => prev.map(r => r._id === updated._id ? updated : r));
          }}
        />
      )}

      {/* NEW ONBOARDING FORM */}
      {showForm && (
        <div style={modalBg} onClick={() => setShowForm(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ margin: 0 }}>New Staff Onboarding</h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <label style={lbl}>Full Name *</label>
            <input value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} style={inputStyle} placeholder="Kavita Sharma" />
            <label style={lbl}>Role</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inputStyle}>
              <option value="doctor">Doctor</option>
              <option value="nurse">Nurse</option>
              <option value="receptionist">Receptionist</option>
              <option value="lab">Lab Technician</option>
              <option value="housekeeping">Housekeeping</option>
              <option value="other">Other</option>
            </select>
            <label style={lbl}>Joining Date</label>
            <input type="date" value={form.joiningDate} onChange={e => setForm(f => ({ ...f, joiningDate: e.target.value }))} style={inputStyle} />
            <label style={lbl}>Mentor / Reporting Manager</label>
            <input value={form.mentorName} onChange={e => setForm(f => ({ ...f, mentorName: e.target.value }))} style={inputStyle} placeholder="Dr. Siddharth" />
            <p style={{ fontSize: 12, color: theme.textMuted, marginTop: 12 }}>
              Default 22-item checklist automatically create ho jayegi (documents, training, observation, kit, legal, signoff).
            </p>
            <button onClick={createRecord} style={{ ...btnStyle, width: '100%', marginTop: 12 }}>
              Create Onboarding Record
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper
function groupByCategory(items) {
  const order = ['documents', 'training', 'observation', 'kit', 'legal', 'signoff'];
  const map = {};
  items.forEach(i => { (map[i.category || 'other'] = map[i.category || 'other'] || []).push(i); });
  const ordered = {};
  order.forEach(k => { if (map[k]) ordered[k] = map[k]; });
  Object.keys(map).forEach(k => { if (!ordered[k]) ordered[k] = map[k]; });
  return ordered;
}

const modalBg = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const modalBox = { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 460, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: theme.textMuted, margin: '10px 0 4px' };

export default StaffOnboardingPage;
